"use client";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./MileageCalculatorSection.module.scss";
import Text from "@components/atoms/Text";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import MileageModal from "@components/molecules/MileageModal";
import { PAGE_TYPES } from "@constants/pageTypes";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getCarAvailability } from "@utils/carAvailability";
import { calculateFuelCost } from "@helpers/fuelCostCalculatorHelper";
import { VariantDetail } from "@interfaces/mileage";
import { SliderInput } from "@components/molecules/SliderInput";

interface MileageCalculatorClientProps {
  mileageData: any;
  cityName: string;
  modelName: string;
  makeName: string;
  isSellable: boolean;
  carAvailability?: string;
}

const MileageCalculatorClient = ({
  mileageData,
  cityName,
  modelName,
  makeName,
  isSellable,
  carAvailability
}: MileageCalculatorClientProps) => {
  const [dailyDistance, setDailyDistance] = useState<number>(40);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedVariant, setSelectedVariant] = useState<VariantDetail | null>(
    null
  );

  useEffect(() => {
    if (mileageData && mileageData.length > 0) {
      setSelectedVariant(mileageData[0]);
    }
  }, [mileageData]);

  const monthlyFuelCost = useMemo(() => {
    if (selectedVariant) {
      return calculateFuelCost(selectedVariant, dailyDistance);
    }
    return "N/A";
  }, [selectedVariant, dailyDistance]);

  if (!selectedVariant) {
    return null;
  }

  const handleDailyDistance = async (e: number) => {
    setDailyDistance(e);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.MILEAGE_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_edit_distance",
        section: "Fuel cost calculator",
        car_availability: carAvailability || getCarAvailability(`${isSellable}`),
        city_name: cityName,
        vehicle_make: makeName,
        vehicle_model: modelName,
      },
    });
  };
  return (
    <div className={styles.calculatorWrapper}>
      <div className={styles.calculatorContent}>
        <div
          className={styles.variantSelector}
          onClick={() => setShowModal(!showModal)}
        >
          <Text className={styles.variantLabel}>Variant</Text>
          <Text className={styles.variantText}>
            {selectedVariant.name}{" "}
            <span>
              {`(${selectedVariant.fuelType}, ${selectedVariant.transmission})`}
            </span>
          </Text>
          {!showModal ? (
            <Image
              src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
              alt="Chevron_down"
              width={24}
              height={24}
              loading="lazy"
            />
          ) : (
            <Image
              src={`${assetLoader()}/icons/Chevron_up_dark.svg`}
              alt="Chevron_down"
              width={24}
              height={24}
              loading="lazy"
            />
          )}
        </div>

        <MileageModal
          isOpen={!!showModal}
          onClose={() => setShowModal(false)}
          title="Select a variant"
          variants={mileageData}
          onSelect={(variant: any) => {
            setSelectedVariant(variant);
            setShowModal(false);
          }}
          preventScoll={false}
          isInternalUse={true}
          isSellable={isSellable}
          segmentPageType={PAGE_TYPES.MILEAGE_PAGE}
          segmentActionName="tap_btn_choose_a_variant"
          segmentSection="Fuel cost calculator"
          cityName={cityName}
          brandName={makeName}
          modelName={modelName}
          carAvailability={carAvailability}
        />

        <SliderInput
          label="Daily distance travelled (in km)"
          value={dailyDistance}
          min={20}
          max={200}
          step={1}
          suffix=" km"
          onChange={(e: any) => handleDailyDistance(Number(e))}
        />

        <div className={styles.costCalculator}>
          <Text className={styles.monthlyCost}>
            Monthly fuel cost<span>₹{monthlyFuelCost}</span>
          </Text>
          <hr className={styles.divider} />
          <div className={styles.costDetail}>
            <Text className={styles.label}>{selectedVariant?.mileageType}</Text>
            <Text className={styles.value}>
              {selectedVariant?.mileage} kmpl
            </Text>
          </div>
          <div className={styles.costDetail}>
            <Text className={styles.label}>
              Today&apos;s fuel cost in {cityName}
            </Text>
            <Text className={styles.value}>₹{selectedVariant?.fuelRate}</Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MileageCalculatorClient;
